/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable no-param-reassign */
import axios, { AxiosHeaders, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios';
import Router from '@/router';
import store from '@/store';
import i18n from '@/i18n';
import { applySecurityRequest } from '@/middleware/axios-security';
import { applyAuthRequest, createAuthErrorHandler } from '@/middleware/axios-auth';
import { resolveSuccessNotification } from '@/middleware/axios-policy';
import {
  attachLoadingKey,
  shouldHandleLoading,
  scheduleLoading,
  finishLoading,
  showAlert,
  pickBusinessStatus,
  httpFallbackMsg,
  asExtCfg,
  asExtRespCfg
} from '@/middleware/axios-ui';

const forceLogout = async (msg?: string) => {
  try {
    // keep behavior consistent with old js
  } catch (_) {
    /* empty */
  }

  try {
    await store.dispatch('Logout');
  } finally {
    try {
      Router.replace('/login');
    } catch (_) {
      Router.push('/login');
    }

    if (msg) {
      showAlert(undefined, { msg, color: 'danger' });
    }
  }
};

// SETUP GUARD
let __isSetup = false;

function getAcceptLanguage(): string {
  const g: any = (i18n as any)?.global;

  const raw = g?.locale;
  const fromI18n = typeof raw === 'string' ? raw : raw?.value;

  return (fromI18n && String(fromI18n)) || 'en';
}

const ensureHeaders = (req: InternalAxiosRequestConfig) => {
  if (!(req.headers instanceof AxiosHeaders)) {
    req.headers = AxiosHeaders.from(req.headers || {});
  }
  return req.headers;
};

export default function setup() {
  if (__isSetup) return;
  __isSetup = true;

  const authErrorHandler = createAuthErrorHandler();

  // -------------------- REQUEST --------------------
  axios.interceptors.request.use(
    async (request: InternalAxiosRequestConfig) => {
      const cfg = asExtCfg(request);

      if (cfg) {
        cfg.__requestStartedAt = Date.now();
      }

      attachLoadingKey(cfg);

      if (shouldHandleLoading(cfg)) {
        scheduleLoading(cfg, 300);
      }

      applySecurityRequest(request as any);
      applyAuthRequest(request as any);

      const locale = getAcceptLanguage();
      ensureHeaders(request).set('Accept-Language', locale);

      return request;
    },
    err => {
      const cfg = asExtCfg(err?.config);
      if (cfg && shouldHandleLoading(cfg)) finishLoading(cfg);
      return Promise.reject(err);
    }
  );

  // -------------------- RESPONSE --------------------
  axios.interceptors.response.use(
    async (response: AxiosResponse) => {
      const cfg = asExtRespCfg(response);

      if (shouldHandleLoading(cfg)) finishLoading(cfg);

      // BYPASS: download/binary responses (blob/arraybuffer)
      const rt = (cfg as any)?.responseType;
      const isBinary =
        rt === 'blob' ||
        rt === 'arraybuffer' ||
        (typeof Blob !== 'undefined' && response?.data instanceof Blob) ||
        (typeof ArrayBuffer !== 'undefined' && response?.data instanceof ArrayBuffer);

      if (isBinary) {
        return response;
      }

      const { isOk, code, message } = pickBusinessStatus(response?.data);

      if (isOk) {
        const override = cfg?.__successToast;
        const shouldShowSuccess = resolveSuccessNotification(cfg?.url, override);

        if (shouldShowSuccess) {
          showAlert(cfg, { msg: message || 'Success', color: 'success' });
        }

        return response;
      }

      (response as any).__handled = true;

      // 1002/1003 = session ended (e.g. duplicate login) -> log out, don't refresh.
      // Token expiry comes as HTTP 401 and is refreshed in axios-auth instead.
      // Refreshing here caused an endless loop and never logged the old tab out.
      if (code === 1002 || code === 1003 || code === '1002' || code === '1003') {
        await forceLogout((message as any) || 'Your session has expired. Please login again.');
        return response;
      }

      showAlert(cfg, {
        msg: message || 'Something went wrong. Please try again.',
        color: 'danger'
      });

      return response;
    },

    async err => {
      const cfg = asExtCfg(err?.config);

      if (cfg && shouldHandleLoading(cfg)) finishLoading(cfg);

      if ((axios as any).isCancel?.(err) || err?.code === 'ERR_CANCELED') {
        return Promise.reject(err);
      }

      try {
        const maybe = await authErrorHandler(err);
        if (maybe) return maybe;
      } catch (e: any) {
        if (err?.response?.status === 401) {
          return Promise.reject(e);
        }
      }

      if (!err || !err.response) {
        showAlert(cfg, {
          msg: "Can't connect to server. Please try again later",
          color: 'danger'
        });
        return Promise.reject(err);
      }

      const { status } = err.response;
      let apiMsg = err.response?.data?.status?.message || err.response?.data?.message || err.message;

      const data = err.response?.data;
      const ct = String(err.response?.headers?.['content-type'] ?? '').toLowerCase();
      const isBlobErr = typeof Blob !== 'undefined' && data instanceof Blob;

      if (isBlobErr && ct.includes('application/json')) {
        try {
          const text = await data.text();
          const json = JSON.parse(text);
          apiMsg = json?.status?.message || json?.message || json?.msg || apiMsg;
        } catch (_) {
          /* empty */
        }
      }

      showAlert(cfg, {
        msg: apiMsg || httpFallbackMsg(status),
        color: 'danger'
      });

      return Promise.reject(err);
    }
  );
}
